<section class="picker w-full mb-6" aria-labelledby="seat-map-title">
  <header class="mb-3">
    <p class="text-xs uppercase tracking-widest text-gray-500 mb-1">
      Your view of {{ seatMap.venueName }}
    </p>
    <h3 id="seat-map-title" class="text-xl font-semibold text-gray-900">Find your view.</h3>
  </header>

  <div
    *ngIf="tiers.length > 1"
    class="flex flex-wrap gap-2 mb-3"
    role="group"
    aria-label="Show only one ticket type"
  >
    <button
      type="button"
      class="tier-chip"
      [class.tier-chip--on]="tierFilter === null"
      [attr.aria-pressed]="tierFilter === null"
      (click)="setTier(null)"
    >
      All bays
    </button>
    <button
      *ngFor="let tier of tiers; trackBy: trackTier"
      type="button"
      class="tier-chip"
      [class.tier-chip--on]="tierFilter === tier.ticketTypeUid"
      [attr.aria-pressed]="tierFilter === tier.ticketTypeUid"
      (click)="setTier(tier.ticketTypeUid)"
    >
      <span class="tier-dot" [style.background]="tier.colour"></span>
      {{ tier.name }}
      <span *ngIf="tierPrice(tier)" class="text-gray-500">{{ tierPrice(tier) }}</span>
    </button>
  </div>

  <div
    #host
    class="seat-map"
    role="group"
    [attr.aria-label]="'Seating map of ' + seatMap.venueName"
    [innerHTML]="safeSvg"
    (click)="onClick($event)"
    (keydown)="onKey($event)"
    (mouseover)="onHover($event)"
    (focusin)="onHover($event)"
    (mouseleave)="onLeave()"
  ></div>

  <div class="bay-card" aria-live="polite">
    <ng-container *ngIf="shownBay as bay; else hint">
      <div class="flex items-start justify-between gap-3">
        <div>
          <p class="font-semibold text-gray-900">{{ title(bay) }}</p>
          <p *ngIf="bay.ticketTypeName" class="text-sm text-gray-700">
            <span class="tier-dot" [style.background]="tierColour(bay)"></span>
            {{ bay.ticketTypeName }}
            <ng-container *ngIf="price(bay)"> · {{ price(bay) }}</ng-container>
          </p>
        </div>
        <span
          class="status-pill"
          [class.status-pill--ok]="bay.status === 'AVAILABLE' && !filteredOut(bay)"
          [class.status-pill--no]="bay.status !== 'AVAILABLE' || filteredOut(bay)"
        >
          {{ filteredOut(bay) ? 'Hidden by filter' : status(bay) }}
        </span>
      </div>
      <p class="text-xs text-gray-500 mt-1">
        <ng-container *ngIf="bay.gate">{{ bay.gate }}</ng-container>
        <ng-container *ngIf="bay.gate && bay.rowCount"> · </ng-container>
        <ng-container *ngIf="bay.rowCount">{{ bay.rowCount }} rows</ng-container>
      </p>
      <p *ngIf="filteredOut(bay)" class="text-sm text-gray-700 mt-2">
        This is not a {{ filterName }} bay. Choose All bays to pick it.
      </p>
      <p *ngIf="selectedBay?.code === bay.code" class="text-sm font-medium text-green-800 mt-2">
        Selected
      </p>
    </ng-container>
    <ng-template #hint>
      <p class="text-sm text-gray-600">
        <span class="font-semibold text-gray-900">Select a bay</span>
        to see its price and how many seats are left.
      </p>
    </ng-template>
  </div>

  <!-- Outside the card on purpose: it stays while the pointer moves over other bays, and the page
       below does not shift as the card's content changes. -->
  <button *ngIf="selectedBay as picked" type="button" class="continue-btn" (click)="proceed.emit()">
    Choose how many tickets
    <span class="sr-only"> for {{ title(picked) }}</span>
  </button>

  <details class="bay-list" [attr.open]="variant === 'branded' ? '' : null">
    <summary>
      <ng-container *ngIf="variant === 'branded'; else plainSummary">
        <span>Or choose a bay below</span>
        <span class="bay-list__count">{{ bays.length }} verified bays</span>
      </ng-container>
      <ng-template #plainSummary>
        Choose from a list
        <span class="text-gray-500">({{ pickableBays.length }} on sale)</span>
      </ng-template>
    </summary>
    <ul *ngIf="listBays.length > 0" class="bay-list__items">
      <li *ngFor="let bay of listBays; trackBy: trackBay">
        <button
          type="button"
          class="bay-row"
          [class.bay-row--on]="selectedCode === bay.code"
          [class.bay-row--off]="!canPick(bay)"
          [style.background]="variant === 'branded' ? tileFill(bay) : null"
          [disabled]="variant === 'branded' && !canPick(bay)"
          [attr.aria-pressed]="selectedCode === bay.code"
          [attr.aria-label]="variant === 'branded' ? title(bay) + ', ' + tileLabel(bay) : null"
          (click)="choose(bay.code)"
        >
          <span class="tier-dot" [style.background]="tierColour(bay)"></span>
          <span class="bay-row__main">
            <span class="font-semibold text-gray-900">{{ variant === 'branded' ? bay.code : title(bay) }}</span>
            <span *ngIf="variant === 'branded'" class="bay-row__label">{{ tileLabel(bay) }}</span>
            <span *ngIf="variant !== 'branded'" class="text-sm text-gray-700">
              {{ bay.ticketTypeName }}<ng-container *ngIf="price(bay)"> · {{ price(bay) }}</ng-container>
            </span>
          </span>
          <span class="bay-row__meta">
            {{ status(bay) }}<ng-container *ngIf="bay.gate"> · {{ bay.gate }}</ng-container>
          </span>
        </button>
      </li>
    </ul>
    <p *ngIf="listBays.length === 0" class="text-sm text-gray-600 px-1 py-3">
      No bays are on sale right now.
    </p>
  </details>

  <ul class="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-600" aria-label="Key">
    <li *ngFor="let tier of tiers" class="flex items-center gap-1">
      <span class="tier-dot" [style.background]="tier.colour"></span>{{ tier.name }}
      <b *ngIf="variant === 'branded' && tierPrice(tier)" class="legend-price">{{ tierPrice(tier) }}</b>
    </li>
    <li class="flex items-center gap-1"><span class="tier-dot tier-dot--grey"></span>Sold out or not on sale</li>
    <li class="flex items-center gap-1"><span class="tier-dot tier-dot--dashed"></span>Closed</li>
  </ul>
</section>
